import { FONT_SIZE_KEYS } from "constants/theme";
import React from "react";
import styled, { css } from "styled-components";
import { mediaMax } from "utils/theme";

const baseStyles = css`
  margin: 0;
`;

const baseHeadingStyles = css`
  ${baseStyles}
  -webkit-font-smoothing: antialiased;

  font-family: ${(props) => props.theme.fonts.heading};
`;

const baseBodyStyles = css`
  ${baseStyles}

  font-weight: ${(props) => props.theme.fontWeights.normal};
  font-family: ${(props) => props.theme.fonts.body};
`;

const getHeadingStyles = (size, theme) => css`
  font-weight: ${
    ["xxl", "xl", "lg", "md"].includes(size)
      ? theme.fontWeights.black
      : theme.fontWeights.bold
  };
  font-size: ${(props) => props.theme.fontSizes[size]}px;
  letter-spacing: ${(props) => props.theme.letterSpacings[size]}em;
  line-height: ${(props) => props.theme.lineHeights[size]};
`;

interface Props extends React.HTMLAttributes<HTMLElement> {
  tag?: React.ElementType;
  color?: string;
  children?: React.ReactNode;
  renderHtml?: boolean;
}

const Text: React.FC<Props> = ({
  tag = "h1",
  children,
  renderHtml,
  ...props
}) => {
  const Component = tag;
  return renderHtml ? (
    <Component {...props} dangerouslySetInnerHTML={{ __html: children }} />
  ) : (
    <Component {...props}>{children}</Component>
  );
};

const StyledText = styled(Text)`
  color: ${(props) => props.theme.colors[props.color] || props.color};

  a {
    color: ${(props) => props.theme.colors.accent};

    font-weight: ${(props) => props.theme.fontWeights.bold};
  }
`;

export const Heading: { [key: string]: React.FC<Props> } = new Array(5)
  .fill(0)
  .reduce(
    (acc, _, index) => ({
      ...acc,
      [`H${index + 1}`]: styled(
        ({ tag = `h${index + 1}`, children, ...props }) => (
          <StyledText tag={tag as React.ElementType} {...props}>
            {children}
          </StyledText>
        )
      )`
        ${baseHeadingStyles}

        ${(props) =>
          getHeadingStyles(
            FONT_SIZE_KEYS[FONT_SIZE_KEYS.length - index - 1],
            props.theme
          )}

        ${mediaMax("lg")} {
          font-size: ${(props) =>
            props.theme.fontSizesMobile[
              FONT_SIZE_KEYS[FONT_SIZE_KEYS.length - index - 1]
            ]}px;
        }
      `,
    }),
    {}
  );

export const Eyebrow: React.FC<Props> = styled(
  ({ tag = "h2", children, color = "accent", ...props }) => (
    <StyledText tag={tag as React.ElementType} color={color} {...props}>
      {children}
    </StyledText>
  )
)`
  ${baseStyles}

  font-size: ${(props) => props.theme.fontSizes.xs}px;
  line-height: ${(props) => props.theme.lineHeights.xs};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  letter-spacing: 0.11em;
  text-transform: uppercase;

  ${mediaMax("lg")} {
    font-size: ${(props) => props.theme.fontSizesMobile.xs}px;
  }
`;

export const BodyLarge: React.FC<Props> = styled(
  ({ tag = "p", children, color = "textBody", ...props }) => (
    <StyledText tag={tag as React.ElementType} color={color} {...props}>
      {children}
    </StyledText>
  )
)`
  ${baseBodyStyles}

  line-height: ${(props) => props.theme.lineHeights.md};
  font-size: ${(props) => props.theme.fontSizes.md}px;

  ${mediaMax("lg")} {
    font-size: ${(props) => props.theme.fontSizesMobile.md}px;
  }
`;

export const Body: React.FC<Props> = styled(
  ({ tag = "p", children, color = "textBody", ...props }) => (
    <StyledText tag={tag as React.ElementType} color={color} {...props}>
      {children}
    </StyledText>
  )
)`
  ${baseBodyStyles}

  line-height: ${(props) => props.theme.lineHeights.sm};
  font-size: ${(props) => props.theme.fontSizes.sm}px;

  ${mediaMax("lg")} {
    font-size: ${(props) => props.theme.fontSizesMobile.sm}px;
  }
`;

export const BodySmall: React.FC<Props> = styled(
  ({ tag = "p", children, color = "textBody", ...props }) => (
    <StyledText tag={tag as React.ElementType} color={color} {...props}>
      {children}
    </StyledText>
  )
)`
  ${baseBodyStyles}

  line-height: ${(props) => props.theme.lineHeights.xs};
  font-size: ${(props) => props.theme.fontSizes.xs}px;

  ${mediaMax("lg")} {
    font-size: ${(props) => props.theme.fontSizesMobile.xs}px;
  }
`;
